import React, { ReactElement, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Button,
  Alert,
  Card,
  Snackbar,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { CopyIcon } from 'src/v3/core/icons'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { useOldQuizData } from './utils/use-old-quiz-data'

interface QuizDataModalProps {
  open: boolean
  onClose: () => void
  eventName: string
}

export function QuizDataModal({
  open,
  onClose,
  eventName,
}: QuizDataModalProps): ReactElement {
  let { quizData, isLoading, error, fetchQuizData } = useOldQuizData()
  let [copiedIndex, setCopiedIndex] = useState<string | null>(null)

  React.useEffect(() => {
    if (open && eventName) {
      fetchQuizData(eventName)
    }
  }, [open, eventName, fetchQuizData])

  let handleCopyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(id)
      setTimeout(() => setCopiedIndex(null), 2000)
    })
  }

  let handleClose = () => {
    setCopiedIndex(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, maxHeight: '90vh' },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">Migrated Quiz Data - {eventName}</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Temporary Disclaimer */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
              mb: 0.5,
            }}
          >
            Temporary Migration Helper
          </Typography>
          <Typography color="text.secondary" variant="caption">
            During the recent data migration, quiz question, answer, and
            explanation text was truncated to varchar(255). Use this utility to
            verify that no important content was cut off. This is a temporary
            tool for admin review purposes.
          </Typography>
        </Alert>

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              py: 4,
            }}
          >
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading quiz data...</Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Alert severity="error">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                mb: 0.5,
              }}
            >
              Failed to Load Quiz Data
            </Typography>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !error && quizData.length === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'background.paperDark',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Quiz Data Found
            </Typography>
            <Typography color="text.secondary" variant="body2">
              This event may not have any quiz data or the quiz was not
              migrated.
            </Typography>
          </Paper>
        )}

        {/* Quiz Data List */}
        {!isLoading && !error && quizData.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {quizData.map((item, qIndex) => (
              <Card
                key={qIndex}
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {/* Question */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    color="primary"
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Question {qIndex + 1}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'background.paperDark',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.questionText}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopyToClipboard(item.questionText, `q-${qIndex}`)
                      }
                      sx={{
                        flex: '0 0 auto',
                        mt: 0.5,
                      }}
                      title="Copy to clipboard"
                    >
                      <CopyIcon
                        sx={{
                          fontSize: 18,
                          color:
                            copiedIndex === `q-${qIndex}`
                              ? 'success.main'
                              : 'action.active',
                        }}
                      />
                    </IconButton>
                  </Paper>
                </Box>

                {/* Choices */}
                {item.choices && item.choices.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Answer Choices ({item.choices.length})
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {item.choices.map((choice, cIndex) => (
                        <Paper
                          key={cIndex}
                          sx={{
                            p: 1.5,
                            bgcolor: 'background.paperDark',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              wordBreak: 'break-word',
                            }}
                          >
                            {choice.text}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopyToClipboard(
                                choice.text,
                                `c-${qIndex}-${cIndex}`,
                              )
                            }
                            sx={{
                              flex: '0 0 auto',
                            }}
                            title="Copy to clipboard"
                          >
                            <CopyIcon
                              sx={{
                                fontSize: 16,
                                color:
                                  copiedIndex === `c-${qIndex}-${cIndex}`
                                    ? 'success.main'
                                    : 'action.active',
                              }}
                            />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Explanations */}
                {item.explanations && item.explanations.length > 0 && (
                  <Box>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Explanations ({item.explanations.length})
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {item.explanations.map((explanation, eIndex) => (
                        <Paper
                          key={eIndex}
                          sx={{
                            p: 1.5,
                            bgcolor: 'background.paperDark',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              wordBreak: 'break-word',
                              fontStyle: 'italic',
                            }}
                          >
                            {explanation.text}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopyToClipboard(
                                explanation.text,
                                `e-${qIndex}-${eIndex}`,
                              )
                            }
                            sx={{
                              flex: '0 0 auto',
                            }}
                            title="Copy to clipboard"
                          >
                            <CopyIcon
                              sx={{
                                fontSize: 16,
                                color:
                                  copiedIndex === `e-${qIndex}-${eIndex}`
                                    ? 'success.main'
                                    : 'action.active',
                              }}
                            />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
      {/* Copy Success Feedback */}
      <Snackbar
        open={copiedIndex !== null}
        autoHideDuration={2000}
        onClose={() => setCopiedIndex(null)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  )
}

export default QuizDataModal
